import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "section-heading" }
const _hoisted_2 = {
  key: 0,
  class: "space-y-3"
}
const _hoisted_3 = { class: "flex items-start gap-2 min-w-0" }
const _hoisted_4 = { class: "contact-name" }
const _hoisted_5 = { class: "contact-info" }
const _hoisted_6 = {
  key: 0,
  class: "info-row"
}
const _hoisted_7 = { class: "info-value" }
const _hoisted_8 = {
  key: 1,
  class: "info-row"
}
const _hoisted_9 = ["href"]
const _hoisted_10 = {
  key: 2,
  class: "info-row"
}
const _hoisted_11 = ["href"]

import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { UserRound, Copy, BriefcaseBusiness, Phone, Mail } from '@lucide/vue';

import { ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';

interface Props {
  contacts: PropertyContact[];
  highlightedContactId?: number | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PropertyContacts',
  props: {
    contacts: {},
    highlightedContactId: { default: null }
  },
  setup(__props: any) {

const props = __props;

const localContacts = ref<PropertyContact[]>([]);
const localHighlightedContactId = ref<number | null>(null);

const snackbar = ref({
  show: false,
  message: '',
  color: 'success',
});

// Watch contacts
let notificationTimer: ReturnType<typeof setTimeout> | null = null;
watch(
  () => snackbar.value,
  (notification) => {
    if (notificationTimer) clearTimeout(notificationTimer);
    if (notification.show)
      notificationTimer = setTimeout(() => {
        snackbar.value.show = false;
      }, 3000);
  }
);
onBeforeUnmount(() => {
  if (notificationTimer) clearTimeout(notificationTimer);
});

// Watch contacts
watch(
  () => props.contacts,
  (newVal) => {
    if (newVal) {
      localContacts.value = JSON.parse(JSON.stringify(newVal));
    } else {
      localContacts.value = [];
    }
  },
  { immediate: true, deep: true }
);

// Watch highlightedContactId
watch(
  () => props.highlightedContactId,
  (newVal) => {
    localHighlightedContactId.value = newVal;
    scrollToHighlightedContact();
  },
  { immediate: true }
);

function scrollToHighlightedContact() {
  if (!localHighlightedContactId.value) {
    return;
  }

  nextTick(() => {
    // In Vue 3 with script setup, refs work differently
    // We need to use template refs directly
    const el = document.querySelector(`[data-contact-id="${localHighlightedContactId.value}"]`);

    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      console.warn(`Contact with id ${localHighlightedContactId.value} not found`);
    }
  });
}

async function copySurveyUrl(contact: PropertyContact) {
  const fullUrl = window.location.origin + contact.survey_url;

  try {
    await navigator.clipboard.writeText(fullUrl);
    snackbar.value = {
      show: true,
      message: `Ссылка для ${contact.name} успешно скопирована`,
      color: 'success',
    };
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка';
    snackbar.value = {
      show: true,
      message: `Ошибка при копировании ссылки: ${errorMessage}`,
      color: 'error',
    };
  }
}

function isHighlighted(contactId: number): boolean {
  return localHighlightedContactId.value !== null && localHighlightedContactId.value === contactId;
}

onMounted(() => {
  scrollToHighlightedContact();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", null, [
    _createElementVNode("div", _hoisted_1, [
      _cache[1] || (_cache[1] = _createElementVNode("h2", null, "Контакты", -1)),
      _createVNode(_unref(Badge), { variant: "outline" }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(localContacts.value ? localContacts.value.length : 0), 1)
        ]),
        _: 1
      })
    ]),
    (localContacts.value?.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(localContacts.value, (contact) => {
            return (_openBlock(), _createBlock(_unref(Card), {
              key: contact.id,
              ref_for: true,
              ref: `contact-${contact.id}`,
              class: _normalizeClass(['contact-card', { 'highlighted-contact': isHighlighted(contact.id) }]),
              "data-contact-id": contact.id
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(CardHeader), { class: "contact-card-header" }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_3, [
                      _createVNode(_unref(UserRound), {
                        size: 16,
                        class: "shrink-0 text-primary mt-0.5"
                      }),
                      _createElementVNode("span", _hoisted_4, _toDisplayString(contact.name), 1)
                    ]),
                    (isHighlighted(contact.id))
                      ? (_openBlock(), _createBlock(_unref(Badge), {
                          key: 0,
                          variant: "success",
                          class: "calling-chip"
                        }, {
                          default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                            _createTextVNode(" Звонит ", -1)
                          ]))]),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    _createVNode(_unref(Button), {
                      variant: "ghost",
                      size: "icon",
                      class: "ml-auto shrink-0",
                      "aria-label": "Скопировать ссылку на анкету",
                      title: "Скопировать ссылку на анкету",
                      onClick: ($event: any) => (copySurveyUrl(contact))
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(Copy), { size: 15 })
                      ]),
                      _: 1
                    }, 8, ["onClick"])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(CardContent), null, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_5, [
                      (contact.position)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                            _createVNode(_unref(BriefcaseBusiness), {
                              size: 14,
                              class: "info-icon"
                            }),
                            _createElementVNode("span", _hoisted_7, _toDisplayString(contact.position), 1)
                          ]))
                        : _createCommentVNode("", true),
                      (contact.phone)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                            _createVNode(_unref(Phone), {
                              size: 14,
                              class: "info-icon"
                            }),
                            _createElementVNode("a", {
                              href: `tel:${contact.phone}`,
                              class: "contact-link"
                            }, _toDisplayString(contact.phone), 9, _hoisted_9)
                          ]))
                        : _createCommentVNode("", true),
                      (contact.email)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                            _createVNode(_unref(Mail), {
                              size: 14,
                              class: "info-icon"
                            }),
                            _createElementVNode("a", {
                              href: `mailto:${contact.email}`,
                              class: "contact-link"
                            }, _toDisplayString(contact.email), 9, _hoisted_11)
                          ]))
                        : _createCommentVNode("", true)
                    ])
                  ]),
                  _: 2
                }, 1024)
              ]),
              _: 2
            }, 1032, ["class", "data-contact-id"]))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    (snackbar.value.show)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: _normalizeClass(["staff-toast", { 'staff-toast--error': snackbar.value.color === 'error' }]),
          role: "status"
        }, [
          _createTextVNode(_toDisplayString(snackbar.value.message) + " ", 1),
          _createVNode(_unref(Button), {
            variant: "ghost",
            size: "sm",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (snackbar.value.show = false))
          }, {
            default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
              _createTextVNode("Закрыть", -1)
            ]))]),
            _: 1
          })
        ], 2))
      : _createCommentVNode("", true)
  ]))
}
}

})